<template>
    <span v-if="visible" class="signoff-submit-wrapper" style="display: inline-block;">
        <button
            v-if="!signedOff"
            type="button"
            class="btn btn-info btn-lg"
            :disabled="pending || submitting"
            @click="onSubmit"
            :title="buttonTitle"
        >
            <i class="fa fa-check-circle-o"></i>
            {{ buttonLabel }}
        </button>
        <span v-else class="signoff-signed-off-text text-muted" style="font-size: 0.9em;">
            <i class="fa fa-check"></i>
            {{ signedOffLabel }}
        </span>
    </span>
</template>

<script>
/*
    Submit-for-Signoff button shown next to Save on the quote edit page.
    Visible only to users with the block1_submitter role per config.
    Clicking POSTs to /quote/{id}/submit-signoff and reloads on success.

    The backend applies the Block1AutoApproval rule and decides whether
    the row gets auto-approved or enters the sales approver's queue. The
    flash message after reload tells the user which branch fired
    (auto-approved vs submitted for approval).

    States (see planning/quote-signoff-design.md §7.5)
      - canSubmit=false or quote past draft  => hidden
      - no existing row, draft               => enabled "Submit for Signoff"
      - pending row exists                   => disabled "Submitted — awaiting sales signoff"
      - row signed off (human)               => "Signed off ✓ by X on DD MMM"
      - row signed off (auto-approved)       => "Auto-approved on DD MMM"
*/
export default {
    props: {
        quoteId: { type: [String, Number], required: true },
        canSubmit: { type: Boolean, required: true },
        quoteStatus: { type: String, required: true },
        existingSignoff: { type: Object, default: null },
        approverName: { type: String, default: '' },
    },
    data() {
        return { submitting: false };
    },
    computed: {
        visible() {
            // Hide entirely for non-submitters or for quotes past draft.
            // (Once a quote is sent/won/etc the Block 1 submit no longer
            // makes sense — auto-approval or the approver's action would
            // already have moved it forward.)
            if (!this.canSubmit) return false;
            if (this.quoteStatus !== 'draft') return false;
            return true;
        },
        pending() {
            return this.existingSignoff && !this.existingSignoff.signed_off_at;
        },
        signedOff() {
            return this.existingSignoff && !!this.existingSignoff.signed_off_at;
        },
        buttonLabel() {
            return this.pending ? 'Submitted — awaiting sales signoff' : 'Submit for Signoff';
        },
        buttonTitle() {
            return this.pending
                ? 'The sales approver has been notified. Refresh to see when approved.'
                : 'Submit this quote for sales signoff';
        },
        signedOffLabel() {
            if (!this.existingSignoff) return '';
            const when = this.formatDate(this.existingSignoff.signed_off_at);
            if (this.existingSignoff.is_auto_approved) {
                return `Auto-approved on ${when}`;
            }
            return `Signed off by ${this.approverName || 'Tim'} on ${when}`;
        },
    },
    methods: {
        onSubmit() {
            if (this.submitting || this.pending || this.signedOff) return;
            this.submitting = true;

            const url = `/quote/${this.quoteId}/submit-signoff`;
            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';

            fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
            })
                .then(r => r.json().then(body => ({ status: r.status, body })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert(body.error || `Submit failed (status ${status})`);
                        this.submitting = false;
                        return;
                    }
                    // Backend writes a flash message on the response side too
                    // when this is a normal browser POST, but we're using fetch
                    // so we provide our own message before reload.
                    const note = body.autoApproved
                        ? 'Auto-approved — routine template-based quote. You can email the customer now.'
                        : 'Submitted for sales signoff. Tim has been notified.';
                    sessionStorage.setItem('signoff_submit_flash', note);
                    window.location.reload();
                })
                .catch(err => {
                    alert('Submit failed: ' + (err && err.message ? err.message : 'network error'));
                    this.submitting = false;
                });
        },
        formatDate(iso) {
            if (!iso) return '';
            const d = new Date(iso);
            return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
        },
    },
    mounted() {
        // Surface any flash note set just before the reload that landed us here.
        const note = sessionStorage.getItem('signoff_submit_flash');
        if (note) {
            sessionStorage.removeItem('signoff_submit_flash');
            // Append to the page if Bootstrap alert area exists; otherwise alert().
            const alertHost = document.querySelector('.alert-host, #flash-messages, .container');
            if (alertHost) {
                const div = document.createElement('div');
                div.className = 'alert alert-info';
                div.textContent = note;
                alertHost.prepend(div);
            } else {
                alert(note);
            }
        }
    },
};
</script>
